import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Password } from 'primereact/password';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import {
  useLazyGetPhoneForResetPassQuery,
  useGetOldPasswordMutation,
  useOtpSendingSmsParGetMutation,
  useResetPassOtpSubmitMutation,
} from './ResetPassword.service';
import { decryptData } from '../../shared/services/cryptoService';
import './reset-password.scss';

/**
 * Derives the userId based on userTypeId.
 * - userTypeId "1" → schoolId
 * - userTypeId "14" → teacherId
 * - otherwise → empty string
 */
export function deriveUserId(
  userTypeId: string,
  schoolId: number | string,
  teacherId: number | string
): string {
  if (userTypeId === '1') {
    return String(schoolId);
  }
  if (userTypeId === '14') {
    return String(teacherId);
  }
  return '';
}

/**
 * Validates a password against the required pattern:
 * At least one uppercase, one lowercase, one digit, length 8-15.
 */
export function validatePassword(value: string): boolean {
  const pattern = /^(?=.*[A-Z])(?=.*[0-9])(?=.*[a-z]).{8,15}$/;
  return pattern.test(value);
}

const ResetPassword: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const toast = useRef<Toast>(null);

  // Session data
  const username = s.username;
  const usertypeid = s.usertypeid;
  const teacher_name = s.teacher_name;
  const schoolName = s.schoolName;
  const schoolId = s.schoolId;
  const teacherId = s.teacherId;

  // Form fields
  const [oldPassword, setOldPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  // UI state
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);
  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [showOtpDialog, setShowOtpDialog] = useState<boolean>(false);
  const [otpValue, setOtpValue] = useState<string>('');
  const [otpMessage, setOtpMessage] = useState<string>('');

  // Data from API
  const [mobileNumber, setMobileNumber] = useState<string>('');
  const [teachName, setTeachName] = useState<string>('');
  const [schlName, setSchlName] = useState<string>('');

  // RTK Query hooks
  const [triggerGetPhone] = useLazyGetPhoneForResetPassQuery();
  const [getOldPassword] = useGetOldPasswordMutation();
  const [otpSendingSmsParGet] = useOtpSendingSmsParGetMutation();
  const [resetPassOtpSubmit] = useResetPassOtpSubmitMutation();

  // Fetch phone details on mount once session is loaded
  useEffect(() => {
    if (!s.loaded) return;

    const userId = deriveUserId(usertypeid, schoolId, teacherId);
    if (!userId) return;

    triggerGetPhone({ userId, userType: usertypeid })
      .unwrap()
      .then((res: any) => {
        if (res?.result && res.result.length > 0) {
          setMobileNumber(res.result[0]['mbl_no'] || '');
          setTeachName(res.result[0]['teacher_name'] || '');
          setSchlName(res.result[0]['school_name'] || '');
        }
      })
      .catch(() => {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not retrieve phone number. Please try again.',
        });
      });
  }, [s.loaded]);

  /**
   * Validates all form fields. Sets submitted to true so inline errors render.
   * Returns true only if all fields are non-empty and new/confirm passwords match pattern.
   */
  const validateForm = (): boolean => {
    setSubmitted(true);

    if (!oldPassword || !newPassword || !confirmPassword) {
      return false;
    }

    if (!validatePassword(newPassword)) {
      return false;
    }

    if (!validatePassword(confirmPassword)) {
      return false;
    }

    return true;
  };

  /**
   * Handles the GET OTP button click.
   * Validates form, checks password rules, verifies old password via API, then sends OTP.
   */
  const handleGetOtp = async (): Promise<void> => {
    if (!validateForm()) {
      return;
    }

    if (newPassword === oldPassword) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'New Password could not be same old password',
      });
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'New Password and Confirm password missmatched',
      });
      return;
    }

    try {
      const res: any = await getOldPassword({
        records: {
          usertype: usertypeid,
          emis_username: username,
          old_password: oldPassword,
        },
      }).unwrap();

      if (res.dataStatus) {
        await sendOtp();
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: res.message,
        });
      }
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'An error occurred. Please try again.',
      });
    }
  };

  /**
   * Sends OTP to the user's registered mobile number after old password verification succeeds.
   */
  const sendOtp = async (): Promise<void> => {
    const otpPayload = {
      records: {
        studentDetails: {
          otp_purpose_id: 6,
          triggered_by_username: username,
          triggered_by_usertype: usertypeid,
          triggered_for_username: username,
          triggered_for_usertype: usertypeid,
          triggered_for_name: teachName,
          triggered_schoolname: schlName,
          sms_strings: newPassword,
        },
        otpTemplate: {
          TemplateId: '1707167782158667552',
          MobileNo: mobileNumber,
          TemplateMsg:
            'OTP to reset password for EMIS Username {#var#} is {#var#}. Do not share with anyone. - TNSED',
          var: {
            var1:
              usertypeid == '1'
                ? schoolName
                : usertypeid == '14'
                  ? teacher_name
                  : username,
            var2: '',
          },
        },
      },
    };

    try {
      const res: any = await otpSendingSmsParGet(otpPayload).unwrap();

      if (res.dataStatus) {
        setOtpMessage(
          `OTP has been successfully sent to your mobile number - ${mobileNumber}. Please enter the OTP within 3 days in the OTP Submission module to reset the password.`
        );
        setShowSuccessDialog(true);
        setOldPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setSubmitted(false);
      } else {
        toast.current?.show({
          severity: 'warn',
          summary: 'Warning',
          detail: res.message,
        });
      }
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'OTP could not be sent. Please try again.',
      });
    }
  };

  /**
   * Closes the OTP dialog and resets related state.
   */
  const handleCloseOtpDialog = (): void => {
    setOtpValue('');
    setShowOtpDialog(false);
    setActiveTabIndex(0);
  };

  /**
   * Handles OTP submission: validates, calls encrypted API, decrypts response.
   */
  const handleSubmitOtp = async (): Promise<void> => {
    if (!otpValue) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please Enter the OTP',
      });
      return;
    }

    const payload = {
      triggered_by_username: username,
      triggered_by_usertype: usertypeid,
      otp_purpose_id: 6,
      otp: otpValue,
    };

    try {
      const res: any = await resetPassOtpSubmit(payload).unwrap();

      const decryptedData = decryptData<{
        dataStatus: boolean;
        message: string;
      }>(res.result);

      if (decryptedData.dataStatus) {
        toast.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: decryptedData.message,
        });
        handleCloseOtpDialog();
      } else {
        toast.current?.show({
          severity: 'warn',
          summary: 'Warning',
          detail: decryptedData.message,
        });
      }
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'OTP submission failed. Please try again.',
      });
    }
  };

  /**
   * Handles tab change — shows OTP dialog when switching to OTP Submission tab.
   */
  const handleTabChange = (index: number): void => {
    setActiveTabIndex(index);
    if (index === 1) {
      setShowOtpDialog(true);
    }
  };

  const passwordSuggestionsFooter = (
    <>
      <p className="mt-2">Suggestions</p>
      <ul className="pl-2 ml-2 mt-0" style={{ lineHeight: '1.5' }}>
        <li>At least one lowercase</li>
        <li>At least one uppercase</li>
        <li>At least one numeric</li>
        <li>Minimum 8 characters</li>
        <li>Maximum 15 characters</li>
      </ul>
    </>
  );

  return (
    <div className="reset-password-container">
      <Toast ref={toast} />
      <Card>
        <TabView
          activeIndex={activeTabIndex}
          onTabChange={(e) => handleTabChange(e.index)}
        >
          <TabPanel header="Reset Password">
            <div className="backclr-grn font-16 mt-4 text-center">
              RESET PASSWORD
            </div>

            <form>
              <div className="row mt-4">
                <div className="col-md-4 col-sm-12"></div>
                <div className="col-md-2 col-sm-12 inMandatory mobileStyle">
                  <label>Old password</label>
                </div>
                <div className="col-md-3 col-sm-12 mobileStyle">
                  <Password
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    toggleMask
                    feedback={false}
                  />
                  {submitted && !oldPassword && (
                    <small className="p-error">Enter Old Password</small>
                  )}
                </div>
                <div className="col-md-3 col-sm-12"></div>
              </div>

              <div className="row mt-3">
                <div className="col-md-4 col-sm-12"></div>
                <div className="col-md-2 col-sm-12 inMandatory mobileStyle">
                  <label>New password</label>
                </div>
                <div className="col-md-3 col-sm-12 mobileStyle">
                  <Password
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    toggleMask
                    footer={passwordSuggestionsFooter}
                  />
                  {submitted && !newPassword && (
                    <small className="p-error">Enter New Password</small>
                  )}
                  {submitted &&
                    newPassword &&
                    !validatePassword(newPassword) && (
                      <small className="p-error">
                        Enter Valid New Password
                      </small>
                    )}
                </div>
                <div className="col-md-3 col-sm-12"></div>
              </div>

              <div className="row mt-3">
                <div className="col-md-4 col-sm-12"></div>
                <div className="col-md-2 col-sm-12 inMandatory mobileStyle">
                  <label>Confirm New password</label>
                </div>
                <div className="col-md-3 col-sm-12 mobileStyle">
                  <Password
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    toggleMask
                    footer={passwordSuggestionsFooter}
                  />
                  {submitted && !confirmPassword && (
                    <small className="p-error">
                      Enter Confirm New Password
                    </small>
                  )}
                  {submitted &&
                    confirmPassword &&
                    !validatePassword(confirmPassword) && (
                      <small className="p-error">
                        Enter Valid Confirm Password
                      </small>
                    )}
                </div>
                <div className="col-md-3 col-sm-12"></div>
              </div>

              <div className="row">
                <div className="col-12 my-3 text-center">
                  <Button
                    label="GET OTP"
                    className="p-button-success"
                    onClick={handleGetOtp}
                  />
                </div>
              </div>
            </form>
          </TabPanel>
          <TabPanel header="OTP Submission"></TabPanel>
        </TabView>
      </Card>

      <Dialog
        header="OTP Sent Successfully"
        visible={showSuccessDialog}
        modal
        draggable={false}
        closable={false}
        onHide={() => setShowSuccessDialog(false)}
      >
        <div className="text-center">
          <h6>{otpMessage}</h6>
          <Button
            label="Ok"
            className="p-button-warning mx-2 mt-2"
            onClick={() => setShowSuccessDialog(false)}
          />
        </div>
      </Dialog>

      <Dialog
        header="OTP Submission"
        visible={showOtpDialog}
        modal
        draggable={false}
        closable={false}
        onHide={() => {}}
      >
        <div className="row">
          <div className="col-md-6 col-12">
            <label className="inMandatory">OTP</label>
            <input
              type="text"
              className="form-control"
              value={otpValue}
              onChange={(e) =>
                setOtpValue(e.target.value.replace(/[^0-9]/g, ''))
              }
              placeholder="Enter the OTP"
              maxLength={6}
            />
          </div>
          <div className="col-12 text-end mt-3">
            <Button
              label="Submit"
              className="p-button-success"
              onClick={handleSubmitOtp}
            />
            <Button
              label="Close"
              className="mx-2"
              onClick={handleCloseOtpDialog}
            />
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default ResetPassword;
